import React from 'react';
import { useTheme } from '../../../contexts/ThemeContext';
import MatchCard from './MatchCard';
import BracketConnector from './BracketConnector';

// Fixed pixel height of the round-label row (e.g. "SEMIFINAL") above each
// column, INCLUDING its bottom margin — exported so a composing layout
// (RegionalBracketView) can compute exactly where a round's matches-box
// starts without guessing at rendered text metrics.
export const ROUND_LABEL_HEIGHT = 24;
export const COMPACT_ROW_HEIGHT = 84; // must match the compact rowHeight below
export const NORMAL_ROW_HEIGHT = 112; // must match the non-compact rowHeight below
// Column/connector widths — exported so a composing layout (RegionalBracketView)
// can compute a region's exact rendered content width instead of guessing a
// fixed container width that may end up a few px too narrow and trigger this
// component's own internal horizontal scrollbar.
export const COMPACT_COL_WIDTH = 136;
export const COMPACT_CONNECTOR_WIDTH = 20;

/**
 * Renders a full single-elimination bracket as horizontally-scrolling round
 * columns, with connector lines drawn between each pair of adjacent rounds.
 * Every column shares the same fixed height and uses
 * `justify-content: space-around` — since each round has exactly half the
 * matches of the previous one, this alone is enough for every match to land
 * vertically centered between the two matches feeding into it, and the
 * connector SVGs reuse that same formula instead of measuring the DOM.
 */
const BracketBoard = ({ rounds, compact = false, title }) => {
  const { theme } = useTheme();
  if (!rounds || rounds.length === 0) return null;

  const rowHeight = compact ? COMPACT_ROW_HEIGHT : NORMAL_ROW_HEIGHT; // header strip + two participant rows
  const colWidth = compact ? 136 : 216;
  const connectorWidth = compact ? 20 : 28;
  // Max across ALL rounds, not just rounds[0] — a mirrored/reversed rounds
  // array (round order flipped so a bracket "grows" leftward) would otherwise
  // size the shared column height off the smallest round instead of the biggest.
  const totalHeight = Math.max(...rounds.map((r) => r.matches.length)) * rowHeight;

  return (
    <div>
      {title && <div className={`mb-2 text-sm font-bold ${theme.text}`}>{title}</div>}
      <div className="flex items-start overflow-x-auto pb-2">
        {rounds.map((round, idx) => {
          const nextRound = rounds[idx + 1];
          // A round can pair into its neighbor in either direction: normally
          // the LEFT round has 2x the matches (converges rightward). In a
          // mirrored/reversed segment (rounds flipped so the bracket grows
          // leftward — e.g. the right-hand side of a converging regional
          // bracket) it's the RIGHT round that has 2x instead, and the
          // connector needs to be drawn — and mirrored — the other way.
          const forward = nextRound && round.matches.length === nextRound.matches.length * 2;
          const backward = nextRound && nextRound.matches.length === round.matches.length * 2;
          return (
            <React.Fragment key={round.id}>
              <div className="flex shrink-0 flex-col" style={{ width: colWidth }}>
                <div
                  className={`flex items-center justify-center text-center text-[11px] font-bold uppercase tracking-wide ${theme.textMuted}`}
                  style={{ height: ROUND_LABEL_HEIGHT }}
                >
                  {round.label}
                </div>
                <div className="flex flex-col justify-around" style={{ height: totalHeight }}>
                  {round.matches.map((match) => (
                    <MatchCard key={match.id} match={match} compact={compact} />
                  ))}
                </div>
              </div>
              {nextRound && (
                <div className="flex shrink-0 flex-col" style={{ width: connectorWidth }}>
                  <div style={{ height: ROUND_LABEL_HEIGHT }} aria-hidden="true" />
                  {(forward || backward) && (
                    <BracketConnector
                      matchCount={forward ? round.matches.length : nextRound.matches.length}
                      totalHeight={totalHeight}
                      width={connectorWidth}
                      colorClassName={theme.textMuted}
                      reversed={backward}
                    />
                  )}
                </div>
              )}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
};

export default BracketBoard;
